import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, Clock, Banknote, ChevronDown, ChevronUp, Briefcase, Send, CalendarDays, Filter, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/config";
import { useAuth } from "@/contexts/AuthContext";
import { JOB_PREFERENCES } from "@/constants";
import { formatDateFr } from "@/lib/formatters";
import { useJobPreferences } from "@/hooks/useJobPreferences";
import { SEO, jobPostingSchema } from "@/components/SEO";
import { toast } from "sonner";
import axios from "axios";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5 } }),
};

function OfferCard({ offer }) {
  const [expanded, setExpanded] = useState(false);
  const [showApply, setShowApply] = useState(false);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const { user } = useAuth();
  const { prefs: dynamicPrefs } = useJobPreferences();
  const JOB_PREFS = dynamicPrefs.length ? dynamicPrefs : JOB_PREFERENCES;

  const handleApply = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/offers/${offer.offer_id}/apply`, { comment }, { withCredentials: true });
      toast.success("Candidature envoyée avec succès !");
      setShowApply(false);
      setComment("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally { setSending(false); }
  };

  const prefLabels = (offer.categories || []).map(c => JOB_PREFS.find(p => p.key === c)?.label || c);

  return (
    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="bg-white border border-gray-200 p-6 md:p-8 card-hover" data-testid={`offer-card-${offer.offer_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="text-lg font-bold text-[#3F3F3F] mb-2">
            {offer.offer_number && <span className="text-xs font-mono text-[#662483] mr-2">{offer.offer_number}</span>}
            {offer.title}
          </h3>
          <div className="flex flex-wrap gap-3 text-sm text-gray-500">
            {offer.location && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {offer.location}</span>}
            {offer.hours && <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {offer.hours}</span>}
            {offer.salary && <span className="flex items-center gap-1"><Banknote className="w-4 h-4" /> {offer.salary}</span>}
            {offer.mission_start && <span className="flex items-center gap-1"><CalendarDays className="w-4 h-4" /> Du {formatDateFr(offer.mission_start)}{offer.mission_end ? ` au ${formatDateFr(offer.mission_end)}` : ""}</span>}
          </div>
          {prefLabels.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {prefLabels.map(l => <Badge key={l} className="rounded-none text-xs bg-[#F0E9F3] text-[#662483]">{l}</Badge>)}
            </div>
          )}
        </div>
        <Badge className="bg-[#E6F0F7] text-[#0069B4] hover:bg-[#E6F0F7] rounded-none text-xs font-semibold">{offer.profile_needed || "Logistique"}</Badge>
      </div>

      {expanded && (
        <div className="mt-4 space-y-4 border-t border-gray-100 pt-4">
          <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{offer.description}</p>
          {offer.requirements && <div><h4 className="text-sm font-bold text-[#3F3F3F] mb-1">Profil recherché</h4><p className="text-sm text-gray-600">{offer.requirements}</p></div>}
          {offer.benefits && <div><h4 className="text-sm font-bold text-[#3F3F3F] mb-1">Ce qu&apos;on vous propose</h4><p className="text-sm text-gray-600">{offer.benefits}</p></div>}

          {/* Apply Section */}
          {!showApply ? (
            user ? (
              <Button onClick={() => setShowApply(true)} className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white font-semibold px-6 py-2 rounded-none hover:opacity-90 text-sm mt-2" data-testid={`apply-btn-${offer.offer_id}`}>
                Postuler maintenant
              </Button>
            ) : (
              <Link to="/auth">
                <Button className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white font-semibold px-6 py-2 rounded-none hover:opacity-90 text-sm mt-2" data-testid={`apply-login-${offer.offer_id}`}>
                  Se connecter pour postuler
                </Button>
              </Link>
            )
          ) : (
            <div className="bg-[#FAFAFA] border border-gray-200 p-4 space-y-3" data-testid={`apply-form-${offer.offer_id}`}>
              <p className="text-sm font-semibold text-[#3F3F3F]">Votre candidature</p>
              <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Ajoutez un commentaire (motivations, disponibilités...)" className="rounded-none text-sm min-h-[80px]" data-testid={`apply-comment-${offer.offer_id}`} />
              <div className="flex gap-2">
                <Button onClick={handleApply} disabled={sending} className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none font-semibold text-sm" data-testid={`apply-submit-${offer.offer_id}`}>
                  <Send className="w-4 h-4 mr-2" /> {sending ? "Envoi..." : "Envoyer ma candidature"}
                </Button>
                <Button variant="outline" onClick={() => setShowApply(false)} className="rounded-none text-sm">Annuler</Button>
              </div>
            </div>
          )}
        </div>
      )}

      <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1 text-sm font-medium text-[#662483] hover:text-[#4B1A61] mt-4 transition-colors" data-testid={`toggle-offer-${offer.offer_id}`}>
        {expanded ? "Voir moins" : "Voir plus"}
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
    </motion.div>
  );
}

export default function OffresPage() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ department: "", city: "", distance_km: "" });

  const fetchOffers = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: "active" };
      if (filters.department) params.department = filters.department;
      if (filters.city && filters.distance_km) {
        params.city = filters.city;
        params.distance_km = filters.distance_km;
      }
      const res = await axios.get(`${API}/offers`, { params });
      setOffers(res.data);
    } catch (err) { console.error("Failed to fetch offers:", err); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchOffers(); }, [fetchOffers]);

  return (
    <main data-testid="offres-page">
      <SEO
        title="Offres d'emploi intérim logistique"
        description={`${offers.length} offres d'emploi en intérim logistique. Cariste, préparateur de commandes, manutentionnaire, chef d'équipe. Postulez en ligne chez FR INTERIM.`}
        path="/offres"
        jsonLd={offers.length > 0 ? {
          "@context": "https://schema.org",
          "@type": "ItemList",
          "itemListElement": offers.slice(0, 10).map((o, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "item": jobPostingSchema(o)
          }))
        } : null}
      />
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://images.pexels.com/photos/14688876/pexels-photo-14688876.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')" }} />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" className="max-w-2xl">
            <motion.p variants={fadeUp} custom={0} className="text-[#E5CD96] text-xs font-bold uppercase tracking-[0.2em] mb-4">Nos offres</motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white mb-6">Offres d&apos;emploi</motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-lg text-gray-200 leading-relaxed">Découvrez les missions disponibles dans le secteur de la logistique.</motion.p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-[#FAFAFA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Barre de filtres géographiques */}
          <div className="bg-white border border-gray-200 p-4 md:p-6 mb-8" data-testid="offers-filter-bar">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-4 h-4 text-[#662483]" />
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#3F3F3F]">Filtrer par localisation</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <Label htmlFor="filter-department" className="text-xs text-gray-600 mb-1 block">Département</Label>
                <Input
                  id="filter-department"
                  data-testid="filter-department-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={2}
                  placeholder="ex. 75"
                  value={filters.department}
                  onChange={(e) => setFilters({ ...filters, department: e.target.value.replace(/\D/g, "").slice(0, 2) })}
                  className="rounded-none text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="filter-city" className="text-xs text-gray-600 mb-1 block">Ville (ou code postal)</Label>
                <Input
                  id="filter-city"
                  data-testid="filter-city-input"
                  type="text"
                  placeholder="ex. Lyon ou 69000"
                  value={filters.city}
                  onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                  className="rounded-none text-sm"
                />
              </div>
              <div>
                <Label htmlFor="filter-distance" className="text-xs text-gray-600 mb-1 block">Rayon (km)</Label>
                <select
                  id="filter-distance"
                  data-testid="filter-distance-select"
                  value={filters.distance_km}
                  onChange={(e) => setFilters({ ...filters, distance_km: e.target.value })}
                  className="w-full h-9 px-2 border border-gray-200 text-sm bg-white rounded-none focus:outline-none focus:ring-2 focus:ring-[#662483]"
                >
                  <option value="">—</option>
                  <option value="10">10 km</option>
                  <option value="20">20 km</option>
                  <option value="30">30 km</option>
                  <option value="50">50 km</option>
                  <option value="100">100 km</option>
                </select>
              </div>
            </div>
            {(filters.department || filters.city || filters.distance_km) && (
              <div className="mt-3 flex items-center justify-between gap-2">
                <p className="text-xs text-gray-500">
                  {filters.city && !filters.distance_km && "Sélectionnez un rayon pour activer le filtre ville."}
                  {filters.city && filters.distance_km && `Ville : ${filters.city} · Rayon : ${filters.distance_km} km`}
                  {filters.department && !filters.city && `Département : ${filters.department}`}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFilters({ department: "", city: "", distance_km: "" })}
                  className="rounded-none text-xs h-8"
                  data-testid="filter-reset-btn"
                >
                  <X className="w-3 h-3 mr-1" /> Réinitialiser
                </Button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center py-12"><div className="w-10 h-10 border-4 border-[#662483] border-t-transparent rounded-full animate-spin"></div></div>
          ) : offers.length === 0 ? (
            <div className="text-center py-16" data-testid="offers-empty-state"><Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" /><h3 className="text-lg font-semibold text-[#3F3F3F] mb-2">Aucune offre disponible</h3><p className="text-sm text-gray-500">{(filters.department || (filters.city && filters.distance_km)) ? "Essayez d\u2019élargir votre zone de recherche." : "Revenez bientôt, de nouvelles offres sont publiées régulièrement."}</p></div>
          ) : (
            <div className="space-y-6">
              <p className="text-sm text-gray-500" data-testid="offers-count">{offers.length} offre{offers.length > 1 ? "s" : ""} disponible{offers.length > 1 ? "s" : ""}</p>
              {offers.map((offer) => <OfferCard key={offer.offer_id} offer={offer} />)}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
